import bcrypt from 'bcryptjs'
import autobind from 'autobind-decorator'
import axios from 'services/axios'
import filtersService from 'services/filters'

export const batchSize = process.env.BATCH_SIZE || 100

@autobind
class ApiService {
  async handleApiError(error = {}) {
    throw error
  }

  async currentUser() {
    const endpoint = 'users/currentUser'

    try {
      const { data } = await axios.get(endpoint, { withCredentials: false })
      const { currentUser, availableZones } = data

      return { currentUser, availableZones }
    } catch (err) {
      throw err
    }
  }

  async dashboardTransmissions() {
    const endpoint = 'dashboard/transmissions'

    try {
      const { data } = await axios.get(endpoint)
      const { events } = data

      return { transmissions: events }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async zones() {
    const endpoint = 'zones'

    try {
      const { data } = await axios.get(endpoint)
      const { zones } = data

      return { zones }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async verticals() {
    const endpoint = 'verticals'

    try {
      const { data } = await axios.get(endpoint)
      const { verticals } = data

      return { verticals }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async groups() {
    const endpoint = 'groups'

    try {
      const { data } = await axios.get(endpoint)
      const { groups } = data

      return { groups }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async createGroup(group) {
    const endpoint = 'groups'

    const { name, zoneIds = [], parentGroupId } = group
    const body = {
      name,
      zoneIds,
      parentGroupId,
    }
    try {
      const { data } = await axios.post(endpoint, body)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async editGroup(group) {
    const endpoint = 'groups'

    const { id, name, zoneIds = [], parentGroupId } = group
    const body = {
      groupId: id,
      group: { name },
      zoneIds,
      parentGroupId,
    }

    try {
      const { data } = await axios.put(endpoint, body)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async deleteGroup(id) {
    const endpoint = 'groups'

    const config = {
      params: {
        id,
      },
    }

    try {
      const { data } = await axios.delete(endpoint, config)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async createZone(zone) {
    const endpoint = 'zones'

    const { name, verticals, groups } = zone

    const body = {
      name,
      verticals,
      groups,
    }

    try {
      const { data } = await axios.post(endpoint, body)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async editZone(zone) {
    const endpoint = 'zones'
    const { id, name, verticals, groups } = zone

    const body = {
      id,
      name,
      verticals,
      groups,
    }

    try {
      const { data } = await axios.put(endpoint, body)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async deleteZone(id) {
    const endpoint = 'zones'

    const config = {
      params: {
        id,
      },
    }

    try {
      const { data } = await axios.delete(endpoint, config)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async organizations() {
    const endpoint = 'organizations'

    try {
      const { data } = await axios.get(endpoint)
      const { organizations } = data

      return { organizations }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async editOrganization(organization) {
    const endpoint = 'organizations'
    const body = {
      organization: {
        id: organization.id,
        name: organization.name,
        alias: organization.alias,
        domains: organization.domains,
      },
    }
    try {
      const { data } = await axios.put(endpoint, body)
      return data
    } catch (err) {
      throw err
    }
  }


  async createOrganization(organization) {
    const endpoint = 'organizations'
    const body = {
      organization: {
        name: organization.name,
        alias: organization.alias,
        domains: organization.domains,
      },
    }
    try {
      const { data } = await axios.post(endpoint, body)
      return data
    } catch (err) {
      throw err
    }
  }

  async deleteOrganization(organizationId) {
    const endpoint = 'organizations'

    const config = {
      params: {
        organizationId,
      },
    }

    try {
      const { data } = await axios.delete(endpoint, config)
      return data
    } catch (err) {
      throw err
    }
  }

  async users() {
    const endpoint = 'users'

    try {
      const { data } = await axios.get(endpoint)
      const { users } = data

      return { users }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async submitUser(user) {
    const endpoint = 'users'

    const isCreate = !user.id
    const method = isCreate ? 'post' : 'put'

    const { id, email, password, fullName, organization, role, groupIds, platforms, firstName, lastName } = user

    try {
      const body = {
        id,
        email: email.toLowerCase(),
        firstName: firstName || fullName?.split(' ')[0] || '',
        lastName: lastName || fullName?.split(' ').slice(1).join(' ') || '',
        organization,
        role,
        groupIds: groupIds || [],
      }

      const { data } = await axios[method](endpoint, body)

      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async deleteUser(userId) {
    const endpoint = 'users'

    const config = {
      params: {
        userId,
      },
    }

    try {
      const { data } = await axios.delete(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async getUserGroups(userId) {
    const endpoint = `users/${userId}/groups`

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async getUserRoles(userId) {
    const endpoint = `users/${userId}/roles`

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async updateUser(userData) {
    const endpoint = 'users'

    try {
      const { data } = await axios.put(endpoint, userData)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async changePassword(currentUser, newPassword) {
    const endpoint = 'users/change-password'

    try {
      const salt = bcrypt.genSaltSync(10)
      const hashedPassowrd = bcrypt.hashSync(newPassword, salt)

      const body = {
        userId: currentUser.id,
        newPassword: hashedPassowrd,
      }

      const { data } = await axios.post(endpoint, body)
      return data
    } catch (err) {
      console.error(err)
      return false
    }
  }

  async updatePassword(currentUser, currentPassword, newPassword) {
    const endpoint = 'users/update-password'

    const body = {
      userId: currentUser.id,
      currentPassword,
      newPassword,
    }

    return await axios.post(endpoint, body)
  }

  async configureMap() {
    const endpoint = 'config/map'

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async configureInitiallySelectedBusinesses() {
    const endpoint = 'config/selected'

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async configurePlatform() {
    const endpoint = 'config/platform'

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async configureStaticFiles() {
    const endpoint = 'config/staticFiles'

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async dashboard(zoneEndpoint) {
    const endpoint = 'dashboard'

    const params = {
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async businessTypes(zoneEndpoint, filters) {
    const endpoint = 'businesses/businessTypes'
    const params = {
      filters: { ...filtersService.getActiveFilters(filters) },
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async businessesSummary(zoneEndpoint) {
    const endpoint = 'businesses/summary'

    const params = {
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async businesses(currentZone, filters = {}, from = 0) {
    const endpoint = 'businesses'

    const params = {
      ...filtersService.getActiveFilters(filters),
      from,
      size: batchSize,
      zone: currentZone?.name,
      zoneEndpoint: currentZone.businessMapperIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      const { businesses, total } = data

      return {
        businesses,
        total,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async businessReview(businessId, from, filters, zoneEndpoint) {
    const endpoint = 'business/reviews'

    if (!businessId) return {}

    const params = {
      businessId,
      from,
      filters,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      const { reviews } = data

      return {
        reviews,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async businessesExport(currentZone, filters = {}, scroll = undefined) {
    const endpoint = 'businesses/export'

    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      scroll_id: scroll,
      zone: currentZone.name,
      zoneEndpoint: currentZone.businessMapperIndices,
    }

    const config = {
      params,
    }
    try {
      const { data } = await axios.get(endpoint, config)
      const { businesses, scrollId } = data

      return {
        businesses,
        scrollId,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async business(currentZone, businessId) {
    const endpoint = 'business'

    const params = {
      businessId,
      zone: currentZone?.name,
      zoneEndpoint: currentZone.businessMapperIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }


  async rentalsHostNames() {
    const endpoint = 'rentals/host-names'

    try {
      const { data } = await axios.get(endpoint)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async rentalsListingsPerHostAggregations(zoneEndpoint) {
    const endpoint = 'rentals/listings-per-host'

    const params = {
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async getMapClusterAggregation(currentZone, top_left, bottom_right, precision, hostFilters) {
    const endpoint = 'rentals/map-cluster-aggregate'

    const params = {
      ...Object.fromEntries(Object.entries(hostFilters).filter(([key, value]) => !!value)),
      top_left,
      bottom_right,
      precision,
      zone: currentZone?.name,
      zoneEndpoint: currentZone?.airbnbIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async rentalsHostsWithMostListings(currentZone, filters = {}) {
    const endpoint = 'rentals/hosts-with-most-listings'

    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      zone: currentZone?.name,
      zoneEndpoint: currentZone?.airbnbIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async rentalsSummary(currentZone) {
    const endpoint = 'rentals/summary'

    const params = {
      zoneEndpoint: currentZone?.airbnbIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async rentalsHosts(currentZone, filters = {}, from = 0) {
    const endpoint = 'rentals/hosts'
    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      from,
      size: 50,
      zone: currentZone?.name,
      zoneEndpoint: currentZone?.airbnbIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)

      const { totalHosts, hosts } = data

      return {
        totalHosts,
        hosts,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async exportHosts(currentZone, filters) {
    const endpoint = 'rentals/rentalsListExport'
    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      zoneEndpoint: currentZone?.airbnbIndices,
    }
    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (error) {
      this.handleApiError(error)
    }
  }

  async rentalsListings(currentZone, filters = {}, from = 0, size = batchSize) {
    const endpoint = 'rentals/listings'

    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      from,
      size,
      zone: currentZone?.name,
      zoneEndpoint: currentZone?.airbnbIndices,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)

      const { totalListings, listings } = data

      return {
        totalListings,
        listings,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async selectedHostsListings(hostId, from, zoneEndpoint) {
    const endpoint = 'rentals/selectedHostListings'

    if (!hostId) return {}

    const params = {
      hostId,
      from,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      const { listings } = data

      return {
        listings,
      }
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async hostsAndListingsLocations(zoneEndpoint) {
    const endpoint = 'rentals/hostsAndListingsLocations'

    const params = {
      zoneEndpoint,
      size: 1,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  // NFT
  async nftEntity(entityId, zoneEndpoint) {
    const endpoint = 'nft'

    if (!entityId) return

    const params = {
      entityId,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async nftEntityExport(entityId, zoneEndpoint) {
    const endpoint = 'nft/entityExport'

    if (!entityId) return

    const params = {
      entityId,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async nftPeriodOverview(entityId, period, zoneEndpoint) {
    const endpoint = 'nft/periodOverview'

    if (!entityId) return

    const params = {
      entityId,
      period,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async nftDocumentedActivity(entityId, period, filters, from, zoneEndpoint) {
    const endpoint = 'nft/documentedActivity'

    if (!entityId) return

    const params = {
      entityId,
      period,
      filters: Object.fromEntries(Object.entries(filters).filter(([key, value]) => !!value)),
      from,
      size: 10,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async fetchGraphData(entityId, zoneEndpoint) {
    if (!entityId || !zoneEndpoint) return

    const endpoint = 'graph'

    const params = {
      entityId,
      zoneEndpoint,
    }

    const config = {
      params,
    }

    try {
      const { data } = await axios.get(endpoint, config)
      return data
    } catch (error) {
      this.handleApiError(error)
    }
  }

  async getTotalDocumentsCount(index) {
    if (!index) return
    const endpoint = 'general/countDocs'
    const params = {
      index,
    }
    const config = { params }
    try {
      const { data: totalDocCount } = await axios.get(endpoint, config)
      return totalDocCount
    } catch (error) {
      this.handleApiError(error)
    }
  }

  async getOrganizations() {
    const endpoint = 'organizations'
    try {
      const { data } = await axios.get(endpoint)
      return data.organizations
    } catch (err) {
      this.handleApiError(err)
    }
  }

  async createUser(userData) {
    const endpoint = 'users'
    try {
      const { data } = await axios.post(endpoint, userData)
      return data
    } catch (err) {
      this.handleApiError(err)
    }
  }
}

export default new ApiService()
